import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import {
    NavBar, List, Badge, Button, Empty,
    InfiniteScroll, PullToRefresh, Tag, Dialog, DotLoading,
} from "antd-mobile";
import {
    getStaffNotifications,
    loadMoreStaffNotifications,
    markStaffNotificationRead,
    markAllStaffNotificationsRead,
    clearStaffNotifications,
} from "../../redux/actions/staffNotification/staffNotification";
import { useNavigate } from "react-router-dom";
import "./StaffNotificationPage.css";

const TYPE_COLOR = {
    "New Appointment": "success",
    "Reschedule": "warning",
    "Cancel": "danger",
    "System": "default",
};

const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const StaffNotificationPage = ({
    notifications = [],
    totalPages,
    getStaffNotifications,
    loadMoreStaffNotifications,
    markStaffNotificationRead,
    markAllStaffNotificationsRead,
    clearStaffNotifications,
}) => {
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Initial load — runs once only
    useEffect(() => {
        const initialLoad = async () => {
            setLoading(true);
            setPage(1);
            await getStaffNotifications();
            setLoading(false);
        };
        initialLoad();
    }, [getStaffNotifications]);

    // Pull-to-refresh handler — separate, stable reference
    const handleRefresh = useCallback(async () => {
        setPage(1);
        await getStaffNotifications();
    }, [getStaffNotifications]);


    const loadMore = useCallback(async () => {
        const nextPage = page + 1;
        await loadMoreStaffNotifications(nextPage);
        setPage(nextPage);
    }, [page, loadMoreStaffNotifications]);

    const handleItemClick = (notification) => {
        if (!notification.read) markStaffNotificationRead(notification._id);
        const link = notification.links?.[0];
        if (link?.url) navigate(link.url);
    };

    const handleClear = () => {
        Dialog.confirm({
            title: "Clear all notifications?",
            content: "This cannot be undone.",
            confirmText: "Confirm",
            cancelText: "Cancel",
            onConfirm: () => {
                clearStaffNotifications();
                navigate(-1);
            },
        });
    };

    return (
        <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100vh", zIndex: 100, background: "white" }}>
            <div className="top-nav">
                <NavBar
                    onBack={() => window.history.length > 2 ? navigate(-1) : navigate("/")}
                    right={
                        notifications.length > 0 && (
                            <div>
                                <Button fill="none" size="small" className="snp-btn-text"
                                    onClick={() => { markAllStaffNotificationsRead(); navigate(-1); }}
                                >
                                    Mark all read
                                </Button>
                                <Button fill="none" size="small" className="snp-btn-text snp-btn-danger"
                                    onClick={handleClear}
                                >
                                    Clear
                                </Button>
                            </div>
                        )
                    }
                >
                    Notifications
                </NavBar>

            </div>

            <div className="snp-body">
                <PullToRefresh onRefresh={handleRefresh}>
                    {loading ? (
                        <div className="snp-loading"><DotLoading color="primary" /></div>
                    ) : notifications.length === 0 ? (
                        <Empty className="snp-empty" description="You're all caught up" />
                    ) : (
                        <>
                            <List className="snp-list">
                                {notifications.map((item) => (
                                    <List.Item
                                        key={item._id}
                                        className={`snp-item ${item.read ? "snp-item--read" : "snp-item--unread"}`}
                                        onClick={() => handleItemClick(item)}
                                        prefix={!item.read && <Badge content={Badge.dot} className="snp-dot" />}
                                        extra={<span className="snp-time">{formatTime(item.createdAt)}</span>}
                                        description={
                                            <div className="snp-desc">
                                                <span>{item.message}</span>
                                                {item.links?.length > 0 && (
                                                    <div className="snp-tags">
                                                        {item.links.map((link) => (
                                                            <Tag key={link.url} color={TYPE_COLOR[item.type] ?? "primary"} fill="outline" className="snp-tag">
                                                                Details
                                                            </Tag>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        }
                                    >
                                        <span className={`snp-title ${item.read ? "" : "snp-title--bold"}`}>
                                            {item.title}
                                        </span>
                                    </List.Item>
                                ))}
                            </List>

                            <InfiniteScroll loadMore={loadMore} hasMore={page < totalPages}>
                                {(hasMore) => hasMore
                                    ? <DotLoading color="primary" />
                                    : <span className="snp-end">No more notifications</span>
                                }
                            </InfiniteScroll>
                        </>
                    )}
                </PullToRefresh>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    notifications: state.staffNotification.notifications,
    totalPages: state.staffNotification.totalPages ?? 1,
});

const mapDispatchToProps = (dispatch) => ({
    getStaffNotifications: () => dispatch(getStaffNotifications()),
    loadMoreStaffNotifications: (page) => dispatch(loadMoreStaffNotifications(page)),
    markStaffNotificationRead: (id) => dispatch(markStaffNotificationRead(id)),
    markAllStaffNotificationsRead: () => dispatch(markAllStaffNotificationsRead()),
    clearStaffNotifications: () => dispatch(clearStaffNotifications()),
});

export default connect(mapStateToProps, mapDispatchToProps)(StaffNotificationPage);